import { useEffect, useState } from 'react';

import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  LinearProgress,
  ListItemText,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import { TimelineDot } from '@material-ui/lab';
import fileSize from 'filesize';

import * as socketEvents from 'constants/socketEvents';
import { getCookie, setCookie } from 'services/cookies';
import socket from 'services/socket';

const useStyles = makeStyles(({ palette, spacing }) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    padding: spacing(1, 3),
    color: palette.text.primary,
    backgroundColor: palette.background.paper,
  },
  linearProgress: {
    minHeight: spacing(2) - 4,
    maxHeight: spacing(2) - 4,
    '& > .MuiLinearProgress-bar': {
      borderRadius: spacing(0, 2, 2, 0),
    },
    '& > .MuiLinearProgress-barColorSecondary': {
      background: `linear-gradient(270deg, ${palette.error.main} 0%, rgba(233, 63, 102, 0) 100.35%) !important`,
    },
  },
  circularProgressContent: {
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  boxContentTotal: {
    display: 'flex',
    alignItems: 'center',
  },
}));

interface StorageInfoInterface {
  alertarLimiteUso: boolean;
  exibirAlertaPermanente: boolean;
  keyAlertaArmazenamento: string;
  limiteTotal: number;
  percentualDisponivel: number;
  percentualEmUso: number;
  totalDisponivel: number;
  totalEmUso: number;
}

const BASE_BIT = 1000; // Padrão de bits utilizado pelas empresas

export default function BarraAlertaArmazenamento() {
  const classes = useStyles();
  const [storageInfo, setStorageInfo] = useState<StorageInfoInterface>();

  const [currentKey, setCurrentKey] = useState(() => {
    return getCookie('keyAlertaArmazenamento') || '';
  });

  const isShowAlert = storageInfo?.alertarLimiteUso;

  useEffect(() => {
    const handler = (data: StorageInfoInterface) => {
      const { keyAlertaArmazenamento } = data;

      setStorageInfo(data);

      // Pega a key atual
      const newCurrentKey = getCookie('keyAlertaArmazenamento');

      // Verifica se a key já foi setada uma vez. Se não seta
      if (!newCurrentKey) {
        setCookie('keyAlertaArmazenamento', keyAlertaArmazenamento);
      }

      /**
       * Checa se a key atual é diferente da nova key que vem no evento.
       * Caso seja, ela é setada nos cookies
       */
      if (newCurrentKey && newCurrentKey !== keyAlertaArmazenamento) {
        setCookie('keyAlertaArmazenamento', keyAlertaArmazenamento);
      }
    };

    socket.on(socketEvents.ON_STORAGE_INFO, handler);

    return () => {
      socket.off(socketEvents.ON_STORAGE_INFO, handler);
      setStorageInfo(undefined);
    };
  }, []);

  const handleClose = () => {
    setStorageInfo(undefined);
    setCurrentKey(keyAlertaArmazenamento);
    setCookie('keyAlertaArmazenamento', keyAlertaArmazenamento);
  };

  if (!storageInfo) return null;

  const {
    percentualEmUso,
    totalEmUso,
    limiteTotal,
    exibirAlertaPermanente,
    keyAlertaArmazenamento,
  } = storageInfo;
  const options = { base: BASE_BIT, round: 0 };
  const totalEmUsoFormat = fileSize(totalEmUso, options);
  const limiteTotalFormat = fileSize(limiteTotal, options);

  const percentualEmUsoValue = percentualEmUso <= 100 ? percentualEmUso : 100;

  const isKeyChange =
    exibirAlertaPermanente || currentKey !== keyAlertaArmazenamento;

  const titleMessage =
    percentualEmUsoValue === 100
      ? 'Armazenamento cheio'
      : 'Armazenamento quase cheio';

  return (
    <Collapse
      in={isShowAlert && isKeyChange}
      mountOnEnter
      unmountOnExit
      collapsedSize="auto"
    >
      <Box height="auto">
        <Box className={classes.root}>
          <Box mr={3} sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress
              color="secondary"
              variant="determinate"
              value={percentualEmUsoValue}
            />
            <Box className={classes.circularProgressContent}>
              <Typography
                variant="caption"
                component="div"
                color="secondary"
              >{`${Math.round(percentualEmUsoValue)}%`}</Typography>
            </Box>
          </Box>

          <ListItemText
            primary={`${titleMessage}!`}
            secondary="Remova arquivos ou entre em contato para liberar mais espaço de armazenamento"
            primaryTypographyProps={{ noWrap: true, variant: 'body2' }}
            secondaryTypographyProps={{
              noWrap: true,
              variant: 'caption',
            }}
          />

          <Box display="flex" alignItems="center">
            <Box mr={1} className={classes.boxContentTotal}>
              <Box mr={1}>
                <TimelineDot color="secondary" />
              </Box>
              <Typography
                color="textSecondary"
                variant="caption"
              >{`${totalEmUsoFormat} usados`}</Typography>
            </Box>

            <Box mr={1} className={classes.boxContentTotal}>
              <Box mr={1}>
                <TimelineDot color="primary" />
              </Box>
              <Typography
                color="textSecondary"
                variant="caption"
              >{`${limiteTotalFormat} no total`}</Typography>
            </Box>

            {!exibirAlertaPermanente && (
              <Box ml={2}>
                <IconButton onClick={handleClose}>
                  <CloseIcon />
                </IconButton>
              </Box>
            )}
          </Box>
        </Box>

        <LinearProgress
          variant="determinate"
          color="secondary"
          value={percentualEmUsoValue}
          className={classes.linearProgress}
        />
      </Box>
    </Collapse>
  );
}
